import { mount } from '../dom-lifecycle';
import {
	COUNTRIES,
	DEFAULT_COUNTRY,
	findByIso,
	findByNumber,
	placeholderFor,
	search,
} from './phone-countries';

/**
 * Wybór kraju przy numerze telefonu.
 *
 * Nad lewą krawędzią pola staje przycisk z flagą i prefiks wybranego kraju,
 * a pod polem rozwija się panel z wyszukiwarką i listą krajów
 * (`flaga  Polska (+48)`). Wybór kraju podmienia flagę, prefiks, maskę numeru
 * i podpowiedź o formacie — wpisane cyfry zostają.
 *
 * W samym polu użytkownik trzyma tylko numer krajowy: prefiks jest osobnym
 * napisem, a nie częścią maski, bo w IMask znak stały „zjada" wpisaną, taką
 * samą cyfrę (przy masce `{+48} 000000000` numer 48 333 12 34 tracił pierwszą
 * czwórkę). Pełny numer — `+48 483331234` — jedzie na serwer ukrytym polem,
 * które przejmuje nazwę pola widocznego, więc formularz po stronie PHP nie
 * zmienia się ani o jotę.
 *
 * Bez JS zostaje pole z prefiksem w podpowiedzi i maską z `data-mask`, wysyłane
 * pod własną nazwą — dokładnie jak przed tą zmianą.
 */
mount('[data-phone-country]', function () {
	const input = this;
	const group = input.closest('.form-control-group') ?? input.parentElement;

	// Numer mógł wrócić z serwera po nieudanej walidacji — wtedy prefiks w polu
	// jest źródłem prawdy o kraju, a nie wartość domyślna. Czytamy atrybut, a nie
	// właściwość, bo maska z attributes.js montuje się pierwsza i zdążyła już
	// przyciąć wartość do samych cyfr.
	const initialValue = input.getAttribute('value') || '';
	const initialCountry = findByNumber(initialValue)
		?? findByIso(input.dataset.phoneCountry)
		?? DEFAULT_COUNTRY;

	let country = initialCountry;

	const toggle = createToggle();
	const prefixLabel = createPrefixLabel();
	const hidden = createHiddenField();
	const panel = createPanel();
	const searchField = panel.querySelector('.phone-country-search');
	const options = [...panel.querySelectorAll('.phone-country-option')];

	group.append(toggle, prefixLabel, hidden);
	group.after(panel);
	panel.id ||= `${input.id}_country_panel`;
	toggle.setAttribute('aria-controls', panel.id);

	apply(country, initialValue);

	toggle.addEventListener('click', () => setOpen(!isOpen()));

	// Każda zmiana treści pola przepisuje pełny numer do pola ukrytego.
	input.addEventListener('input', syncHidden);
	input.addEventListener('change', syncHidden);

	// Wklejony numer międzynarodowy sam ustawia kraj — inaczej maska zostawiłaby
	// z niego pierwsze cyfry prefiksu i obciąłaby koniec numeru.
	input.addEventListener('paste', event => {
		const pasted = event.clipboardData?.getData('text') ?? '';
		const pastedCountry = findByNumber(pasted);
		if (!pastedCountry) {
			return;
		}

		event.preventDefault();
		apply(pastedCountry, pasted);
	});

	// Escape zamyka panel także wtedy, gdy fokus wrócił już do numeru — fokus
	// zostaje wtedy w polu, bo użytkownik właśnie wpisuje numer.
	input.addEventListener('keydown', event => {
		if (event.key === 'Escape' && isOpen()) {
			setOpen(false);
		}
	});

	// Reset formularza wraca do kraju i numeru, z jakimi strona się otworzyła.
	$(input.form).on('reset', () => setTimeout(() => apply(initialCountry, initialValue), 0));

	panel.addEventListener('keydown', event => {
		if (event.key === 'Escape') {
			close();
		} else if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
			event.preventDefault();
			focusNextOption(event.target, event.key === 'ArrowDown' ? 1 : -1);
		}
	});

	searchField.addEventListener('input', () => filter(searchField.value));
	searchField.addEventListener('keydown', event => {
		// Enter w wyszukiwarce nie może wysłać formularza — wybiera pierwszy wynik.
		if (event.key === 'Enter') {
			event.preventDefault();
			visibleOptions()[0]?.click();
		}
	});

	options.forEach(option => option.addEventListener('click', () => {
		apply(findByIso(option.dataset.iso));
		setOpen(false);
		input.focus();
	}));

	/**
	 * Ustawia kraj: flaga, prefiks, maska numeru, podpowiedź i stan na liście.
	 * Wpisany numer krajowy zostaje — przycięty do długości nowego kraju.
	 *
	 * @param {import('./phone-countries').PhoneCountry|undefined} next
	 * @param {string} [source] - numer, z którego brać cyfry (domyślnie treść pola)
	 */
	function apply(next, source = input.value) {
		if (!next) {
			return;
		}

		const digits = nationalDigits(source).slice(0, next.maxDigits);
		country = next;

		toggle.querySelector('img').src = country.flag;
		toggle.setAttribute('aria-label', `Kraj numeru telefonu: ${country.label}. Zmień kraj`);
		prefixLabel.textContent = country.prefix;
		// Treść pola zaczyna się za flagą i prefiksem, więc padding pola musi znać
		// jego długość: `+1` zajmuje mniej miejsca niż `+995`.
		group.style.setProperty('--phone-prefix-length', String(country.prefix.length));
		input.placeholder = placeholderFor(country);
		input.dataset.phoneCountry = country.iso;
		options.forEach(option => option.setAttribute(
			'aria-pressed',
			String(option.dataset.iso === country.iso),
		));

		setDigits(digits);
	}

	/**
	 * Wpisuje numer krajowy w pole — przez maskę, żeby pole i walidacja „mask"
	 * (patrz attributes.js) zostały spójne.
	 *
	 * @param {string} digits - same cyfry numeru krajowego
	 */
	function setDigits(digits) {
		const imask = input._imask;

		if (!imask) {
			input.value = digits;
			syncHidden();

			return;
		}

		imask.updateOptions({ mask: country.mask });
		input.dataset.mask = country.mask;
		imask.value = digits;
		$(input).toggleRuleValid('mask', imask.masked.isComplete);
		syncHidden();
	}

	/** Przepisuje prefiks razem z numerem do ukrytego pola wysyłanego na serwer. */
	function syncHidden() {
		hidden.value = input.value ? `${country.prefix} ${input.value}` : '';
		// Komunikat błędu z serwera wisi na polu ukrytym (to ono nosi nazwę pola,
		// patrz processFormErrors w ajax-form.js), a ukryte pole samo nie zgłasza
		// zmian — bez tego błąd zostałby na ekranie po poprawieniu numeru.
		$(hidden).trigger('change');
	}

	/**
	 * Same cyfry numeru krajowego. Numer podany po międzynarodowemu (`+48 …`,
	 * `0048 …`) traci prefiks; numer krajowy zostaje nietknięty, nawet jeśli
	 * zaczyna się tak samo jak prefiks (48 333 12 34 z Radomia).
	 *
	 * @param {string} source
	 * @returns {string}
	 */
	function nationalDigits(source) {
		const text = String(source ?? '').trim().replace(/^00/, '+');
		const detected = findByNumber(text);
		const digits = text.replace(/\D/g, '');

		return detected
			? digits.slice(detected.prefix.length - 1)
			: digits;
	}

	/**
	 * Zostawia na liście kraje pasujące do frazy (nazwa albo prefiks). Wiersze są
	 * ukrywane, a nie budowane od nowa, żeby flagi nie wczytywały się dwa razy.
	 *
	 * @param {string} phrase
	 */
	function filter(phrase) {
		const matched = new Set(search(phrase).map(item => item.iso));
		options.forEach(option => {
			option.parentElement.hidden = !matched.has(option.dataset.iso);
		});
		panel.querySelector('.phone-country-empty').hidden = matched.size > 0;
	}

	/** @returns {HTMLButtonElement[]} */
	function visibleOptions() {
		return options.filter(option => !option.parentElement.hidden);
	}

	/**
	 * Przesuwa fokus po widocznych wierszach listy; z wyszukiwarki wchodzi na
	 * pierwszy (albo ostatni przy strzałce w górę).
	 *
	 * @param {HTMLElement} current
	 * @param {number} step
	 */
	function focusNextOption(current, step) {
		const visible = visibleOptions();
		const index = visible.indexOf(current);
		const next = index === -1
			? visible[step > 0 ? 0 : visible.length - 1]
			: visible[index + step];

		(next ?? (step > 0 ? visible[0] : searchField))?.focus();
	}

	/** @returns {boolean} */
	function isOpen() {
		return !panel.hidden;
	}

	/**
	 * Kliknięcie poza polem zamyka panel; w panelu i na przycisku z flagą nie,
	 * bo to kliknięcie w listę albo w samo zamykanie.
	 *
	 * @param {MouseEvent} event
	 */
	function closeOnOutsideClick(event) {
		if (!panel.contains(event.target) && !toggle.contains(event.target)) {
			setOpen(false);
		}
	}

	/** @param {boolean} open */
	function setOpen(open) {
		panel.hidden = !open;
		toggle.setAttribute('aria-expanded', String(open));

		if (open) {
			searchField.value = '';
			filter('');
			panel.querySelector('.phone-country-list').scrollTop = 0;
			searchField.focus();
			// Nasłuch tylko na czas otwarcia — inaczej po podmianie formularza
			// (ajax-form.js) zostawałby wiszący nasłuch na nieistniejący panel.
			document.addEventListener('click', closeOnOutsideClick);
		} else {
			document.removeEventListener('click', closeOnOutsideClick);
		}
	}

	function close() {
		if (isOpen()) {
			setOpen(false);
			toggle.focus();
		}
	}

	/** @returns {HTMLButtonElement} */
	function createToggle() {
		const button = document.createElement('button');
		button.type = 'button';
		button.className = 'phone-country-toggle';
		button.setAttribute('aria-expanded', 'false');
		button.innerHTML = `<img src="${country.flag}" alt="" width="30" height="30">`;

		return button;
	}

	/**
	 * Prefiks przed treścią pola. Dla czytników ekranu jest zbędny — kraj razem
	 * z prefiksem czyta etykieta przycisku z flagą.
	 *
	 * @returns {HTMLSpanElement}
	 */
	function createPrefixLabel() {
		const element = document.createElement('span');
		element.className = 'phone-country-prefix';
		element.setAttribute('aria-hidden', 'true');

		return element;
	}

	/**
	 * Ukryte pole przejmuje nazwę pola widocznego, żeby na serwer poszedł pełny
	 * numer z prefiksem. Widoczne pole traci nazwę, ale zostaje z `required`
	 * i maską, więc walidacja w przeglądarce pokazuje błąd tam, gdzie trzeba.
	 *
	 * @returns {HTMLInputElement}
	 */
	function createHiddenField() {
		const element = document.createElement('input');
		element.type = 'hidden';
		element.name = input.name;
		input.removeAttribute('name');

		return element;
	}

	/** @returns {HTMLDivElement} */
	function createPanel() {
		const element = document.createElement('div');
		element.className = 'phone-country-panel';
		element.hidden = true;
		element.innerHTML = `
			<label class="phone-country-search-label">
				<span class="visually-hidden">Wyszukaj kraj</span>
				<input
					type="search"
					class="phone-country-search"
					placeholder="Wyszukaj kraj"
					autocomplete="off"
					autocapitalize="off"
					spellcheck="false"
				>
			</label>
			<p class="phone-country-empty" hidden>Brak kraju o takiej nazwie lub prefiksie</p>
			<ul class="phone-country-list">
				${COUNTRIES.map(item => `
					<li>
						<button type="button" class="phone-country-option" data-iso="${item.iso}" aria-pressed="false">
							<img class="phone-country-flag" src="${item.flag}" alt="" width="30" height="30" loading="lazy" decoding="async">
							<span>${item.label}</span>
						</button>
					</li>
				`).join('')}
			</ul>
		`;

		return element;
	}
});
